/**
 * Laws × systems grid + cell editor — V98-4-5 (PRD G-4; build list V98-4-2
 * [R3]: EU risk tier, interim position, fact questions verbatim), V98-4 review.
 *
 *   grid         one row per law in the AI Regulatory scope, one column per
 *                system in its boundary. A cell shows the OUTCOME (the server
 *                never sends a non-Legal reader the reason). Every cell is a
 *                button (T4-B2): Legal decides / edits; a cell waiting on its
 *                owner reads "Waiting · {owner}" (T4-B1); the asked owner sees
 *                "Answer Legal’s question" (T4-H9, even with the Legal hat);
 *                everyone else opens a read-only sheet with "Ask Legal".
 *                Below 600px: one card per system, a system switcher that
 *                starts on the first system with an open cell (T4-H3).
 *   cell editor  modes, chosen before the first render (T4-H4):
 *                  answer   the asked owner: the question verbatim, an attested
 *                           answer, or "Not me — suggest someone";
 *                  waiting  Legal, a question out: Nudge {owner}, Withdraw
 *                           question…, Record interim position (in force);
 *                  decide   Legal: outcome, conditions, EU role + tier, reason,
 *                           visibility; ask the owner; interim; history;
 *                  view     everyone else: the outcome, one action (Ask Legal).
 *
 * Writes are server-enforced; this component only shows what the caller may do.
 */
import { useEffect, useId, useRef, useState, type ReactElement } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Alert, Button, Checkbox, Field, Input, Radio, Select, Textarea } from '@aigovops/design-system';
import {
  answerFactQuestion, askLegalAboutCell, askOwner, decideCell, getCellHistory, nudgeFactQuestion, passFactQuestion, setInterimPosition, withdrawFactQuestion,
  type AiGrid, type AiGridCell, type AiLaw, type DecideBody, type DeterminationOutcome, type EuRiskTier, type EuRole,
} from '../../lib/ai-program-api';
import { ApiError } from '../../lib/api';
import { OUTCOME_LABEL, ROLE_LABEL, TIER_LABEL, VISIBILITY_LABEL, cellDisplay, cellTitle, jurisdictionName, lawOf, systemName } from '../../lib/ai-program-model';
import { fmtDay } from '../../lib/today-model';
import { ActionDialog, Chip, domId, errorText } from '../today/shared';
import type { Names } from '../../lib/today-model';

const NARROW_GRID = '(max-width: 600px)';
function useNarrowGrid(): boolean {
  const q = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia(NARROW_GRID).matches;
  const [narrow, setNarrow] = useState(q);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return;
    const m = window.matchMedia(NARROW_GRID);
    const update = () => setNarrow(m.matches);
    update();
    m.addEventListener?.('change', update);
    return () => m.removeEventListener?.('change', update);
  }, []);
  return narrow;
}

/** What one cell button says: the visible words (label · sub · action) are its accessible name's start (T4-H5). */
function cellWords(cell: AiGridCell, law: AiLaw, names: Names, isLegal: boolean, viewerId: string) {
  const waitingOn = cell.fact_waiting ? names.get(cell.fact_waiting.asked_to_user_id) ?? 'the owner' : null;
  const d = cellDisplay(cell, law, waitingOn);
  const askedMe = !!cell.fact_waiting && cell.fact_waiting.asked_to_user_id === viewerId;
  const go = askedMe ? 'Answer Legal’s question'
    : isLegal ? (cell.fact_waiting ? null : d.open ? 'Decide' : d.in_draft ? 'Edit' : null)
    : d.open ? 'Ask Legal' : null;
  return { d, go, words: [d.label, d.sub, go].filter(Boolean) as string[] };
}

export const cellButtonId = (cellId: string) => domId('cell', cellId);

export function ApplicabilityGrid({ grid, names, isLegal, viewerId, onOpenCell }: {
  grid: AiGrid; names: Names; isLegal: boolean; viewerId: string; onOpenCell: (cellId: string) => void;
}) {
  const narrow = useNarrowGrid();
  const caseLaw = grid.laws.flatMap((l) => l.case_law.map((cl) => ({ law: l, cl })));
  if (!grid.laws.length || !grid.systems.length) return null;
  const cellAt = (lawKey: string, systemId: string) => grid.cells.find((c) => c.law_key === lawKey && c.system_id === systemId) ?? null;
  const button = (cell: AiGridCell, law: AiLaw, sysName: string, wide = false) => {
    const { d, go, words } = cellWords(cell, law, names, isLegal, viewerId);
    return (
      <button type="button" className={`gp-ai-cellbtn${wide ? ' gp-ai-cellbtn--wide' : ''}`} data-open={d.open || undefined} data-draft={d.in_draft || undefined}
        id={cellButtonId(cell.cell_id)} aria-label={`${words.join(' · ')} · ${law.label} × ${sysName}`} onClick={() => onOpenCell(cell.cell_id)}>
        {wide ? <span className="gp-ai-cell-law">{law.label}</span> : null}
        <span className="gp-ai-cell-label" data-tone={d.tone}>{d.label}</span>
        {d.sub ? <span className="gp-ai-cell-sub">{d.sub}</span> : null}
        {go ? <span className="gp-ai-cellbtn-go">{go}</span> : null}
      </button>
    );
  };
  return (
    <>
      {narrow ? <NarrowGrid grid={grid} cellAt={cellAt} button={button} /> : (
        // T4-H3: a focusable, named scroller; the row header stays put while the systems scroll.
        <div className="gp-ai-grid-wrap" role="region" aria-labelledby="ai-grid-h" tabIndex={0}>
          <table className="gp-ai-grid" aria-labelledby="ai-grid-h">
            <thead>
              <tr>
                <th scope="col">Law</th>
                {grid.systems.map((s) => <th key={s.system_id} scope="col">{s.name}</th>)}
              </tr>
            </thead>
            <tbody>
              {grid.laws.map((law) => (
                <tr key={law.law_key}>
                  <th scope="row">
                    {law.label}
                    <span className="gp-ai-law-cite">{`${jurisdictionName(law.jurisdiction)}${law.in_force ? ' · in force' : law.effective_date ? ` · applies ${fmtDay(law.effective_date)}` : ''}`}</span>
                  </th>
                  {grid.systems.map((s) => {
                    const cell = cellAt(law.law_key, s.system_id);
                    return <td key={s.system_id}>{cell ? button(cell, law, s.name) : null}</td>;
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {/* T4-H3: the case-law effect sits outside the row header — one line per ruling. */}
      {caseLaw.length ? (
        <ul className="gp-ai-caselaw" aria-label="Case law on these laws">
          {caseLaw.map(({ law, cl }) => (
            <li key={cl.link_id}>
              <strong>{cl.case.case_name}</strong>
              <span>{`${law.label} · ${cl.effect_line}`}</span>
              {cl.confirmed ? null : <Chip text="Effect not confirmed" tone="amber" />}
            </li>
          ))}
        </ul>
      ) : null}
    </>
  );
}

/** Below 600px: one card per system (each law a full-width button), starting on the first system with an open cell. */
function NarrowGrid({ grid, cellAt, button }: {
  grid: AiGrid;
  cellAt: (lawKey: string, systemId: string) => AiGridCell | null;
  button: (cell: AiGridCell, law: AiLaw, sysName: string, wide?: boolean) => ReactElement;
}) {
  const uid = useId();
  const openOf = (systemId: string) => grid.cells.filter((c) => c.system_id === systemId && c.open).length;
  const first = grid.systems.find((s) => openOf(s.system_id) > 0) ?? grid.systems[0]!;
  const [pick, setPick] = useState(first.system_id);
  const sys = grid.systems.find((s) => s.system_id === pick) ?? first;
  return (
    <div className="gp-ai-narrow">
      <Field id={`${uid}-sys`} label="System">
        <Select value={sys.system_id} onChange={(e) => setPick(e.currentTarget.value)}>
          {grid.systems.map((s) => {
            const n = openOf(s.system_id);
            return <option key={s.system_id} value={s.system_id}>{`${s.name}${n ? ` · Open ${n}` : ''}`}</option>;
          })}
        </Select>
      </Field>
      <section className="gp-ai-syscard" aria-label={sys.name}>
        <ul className="gp-ai-syslaws">
          {grid.laws.map((law) => {
            const cell = cellAt(law.law_key, sys.system_id);
            return cell ? <li key={law.law_key}>{button(cell, law, sys.name, true)}</li> : null;
          })}
        </ul>
      </section>
    </div>
  );
}

// ---- cell editor ------------------------------------------------------------------------------

const OUTCOMES: DeterminationOutcome[] = ['applies', 'does_not_apply', 'applies_with_conditions'];
const ROLES: EuRole[] = ['provider', 'deployer', 'both'];
const TIERS: EuRiskTier[] = ['high', 'limited', 'minimal', 'prohibited'];
const STATE_WORD: Record<string, string> = { decided: 'In the draft', recorded: 'Recorded', superseded: 'Replaced', interim: 'Interim position' };

type Mode = 'decide' | 'ask' | 'interim' | 'answer' | 'waiting' | 'withdraw' | 'view' | 'ask_legal' | 'not_me';

/** The mode a cell opens in — before the first render (T4-H4), the asked owner first (T4-H9). */
export function initialMode(cell: AiGridCell, isLegal: boolean, viewerId: string): Mode {
  if (cell.fact_waiting && cell.fact_waiting.asked_to_user_id === viewerId) return 'answer';
  if (!isLegal) return 'view';
  return cell.fact_waiting ? 'waiting' : 'decide';
}

export interface CellEditorProps {
  slug: string; grid: AiGrid; cell: AiGridCell | null; isLegal: boolean; viewerId: string; names: Names;
  legalName: string | null;
  open: boolean; onClose: () => void;
  /** After a write: the message to announce (the editor has closed). */
  onSaved: (message: string) => void;
  /** Legal, a recorded cell: open the release dialog with this determination preselected (T4-H7). */
  onRelease?: (determinationId: string) => void;
}

export function CellEditor(props: CellEditorProps) {
  const { cell, open, onClose } = props;
  const law = cell ? lawOf(props.grid, cell.law_key) : null;
  if (!cell || !law) return <ActionDialog open={false} title="" submitLabel="" onClose={onClose} onSubmit={() => false}>{null}</ActionDialog>;
  // Keyed by the cell: the mode and fields are set from the cell before the first render.
  return <CellEditorBody key={`${cell.cell_id}:${open}`} {...props} cell={cell} law={law} />;
}

function CellEditorBody({ slug, grid, cell, law, isLegal, viewerId, names, legalName, open, onClose, onSaved, onRelease }: CellEditorProps & { cell: AiGridCell; law: AiLaw }) {
  const uid = useId();
  const d = cell.determination;
  const isEu = law.jurisdiction === 'EU';
  const ruleChanged = cell.state === 'rule_changed';
  const owner = grid.systems.find((s) => s.system_id === cell.system_id)?.owner_user_id ?? null;
  const ownerName = names.get(owner ?? '') ?? 'the system owner';
  const selfOwner = isLegal && owner === viewerId;
  const startMode = initialMode(cell, isLegal, viewerId);
  const [mode, setMode] = useState<Mode>(startMode);
  // Rule changed: the old outcome and reason belong to the old text — start empty (the alert says what it was).
  const [outcome, setOutcome] = useState<DeterminationOutcome | ''>(d?.outcome && !ruleChanged ? d.outcome : '');
  const [conditions, setConditions] = useState(ruleChanged ? '' : d?.conditions ?? '');
  const [role, setRole] = useState<EuRole | ''>(ruleChanged ? '' : d?.eu_role ?? '');
  const [tier, setTier] = useState<EuRiskTier | ''>(ruleChanged ? '' : d?.eu_risk_tier ?? '');
  const [reason, setReason] = useState(ruleChanged ? '' : d?.reason ?? '');
  const [visibility, setVisibility] = useState<'legal_only' | 'workspace' | 'external'>(d?.visibility ?? 'legal_only');
  const [question, setQuestion] = useState('');
  const [answer, setAnswer] = useState('');
  const [attest, setAttest] = useState(false);
  const [position, setPosition] = useState(d?.interim_position ?? '');
  const [note, setNote] = useState('');
  const [suggest, setSuggest] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [nudged, setNudged] = useState<string | null>(null);
  const history = useQuery({
    queryKey: ['ai-cell-history', slug, cell.cell_id], queryFn: () => getCellHistory(slug, cell.cell_id),
    enabled: open && !!d && mode === 'decide', staleTime: 30_000,
  });

  // T4-H4: a changed mode focuses its first field (Ask, Interim, Withdraw, Back).
  const firstMode = useRef(true);
  useEffect(() => {
    if (firstMode.current) { firstMode.current = false; return; }
    const body = document.querySelector<HTMLElement>('dialog[open] .gp-dialog-body');
    body?.querySelector<HTMLElement>('textarea, input:not([type="hidden"]):not([disabled]), select, button')?.focus();
  }, [mode]);

  const title = cellTitle(grid, cell);
  const draftV = grid.decision?.state === 'draft' ? grid.decision.version : (grid.decision?.version ?? 0) + 1;
  const applies = outcome === 'applies' || outcome === 'applies_with_conditions';
  const asker = cell.fact_waiting ? names.get(cell.fact_waiting.asked_by) ?? legalName ?? 'Legal' : legalName ?? 'Legal';
  const go = (m: Mode) => { setMode(m); setErrors({}); setError(null); };
  const back = (m: Mode) => ({ label: '← Back', onClick: () => go(m) });

  /** After a decision: "… · 2 open", or "all decided · Record decision v4 is ready" (T4-H4). */
  const afterDecide = (label: string) => {
    const left = grid.counts.open - (cell.open ? 1 : 0);
    return left > 0 ? `${title} · ${label} · ${left} open` : `${title} · ${label} · all decided · Record decision v${draftV} is ready`;
  };

  /** Validation → focus the first invalid control (radios, checkbox, fields). */
  const invalid = (errs: Record<string, string>, order: string[]) => {
    setErrors(errs);
    const k = order.find((x) => errs[x]);
    if (k) setTimeout(() => document.querySelector<HTMLElement>(`[data-err="${uid}-${k}"]`)?.focus(), 0);
    return !k;
  };

  const submit = async (): Promise<boolean> => {
    setError(null);
    const errs: Record<string, string> = {};
    if (mode === 'decide') {
      if (!outcome) errs.outcome = 'Choose an outcome.';
      if (outcome === 'applies_with_conditions' && !conditions.trim()) errs.conditions = 'Say which conditions apply.';
      if (isEu && applies && !role) errs.role = 'Choose the EU role.';
      if (isEu && applies && !tier) errs.tier = 'Choose the EU risk tier.';
      if (reason.trim().length < 3) errs.reason = 'Give a one-line reason.';
      if (!invalid(errs, ['outcome', 'conditions', 'role', 'tier', 'reason'])) return false;
    } else if (mode === 'ask') {
      if (question.trim().length < 3) errs.question = 'Write the question.';
      if (selfOwner && !answer.trim()) errs.answer = 'You own this system: record the answer (self-attested).';
      if (!invalid(errs, ['question', 'answer'])) return false;
    } else if (mode === 'interim') {
      if (position.trim().length < 3) errs.position = 'State the interim position.';
      if (!invalid(errs, ['position'])) return false;
    } else if (mode === 'answer') {
      if (!answer.trim()) errs.answer = 'Write your answer.';
      if (!attest) errs.attest = 'Confirm the answer is accurate.';
      if (!invalid(errs, ['answer', 'attest'])) return false;
    } else if (mode === 'withdraw') {
      if (note.trim().length < 3) errs.note = 'Say why.';
      if (!invalid(errs, ['note'])) return false;
    } else if (mode === 'not_me') {
      if (!suggest && note.trim().length < 3) errs.suggest = 'Choose someone, or say who should answer.';
      if (!invalid(errs, ['suggest'])) return false;
    }
    setBusy(true);
    try {
      if (mode === 'decide') {
        const body: DecideBody = {
          outcome: outcome as DeterminationOutcome, reason: reason.trim(), visibility,
          conditions: outcome === 'applies_with_conditions' ? conditions.trim() : null,
          eu_role: isEu && role ? role : null, eu_risk_tier: isEu && applies && tier ? tier : null,
        };
        await decideCell(slug, cell.cell_id, body);
        onSaved(afterDecide(OUTCOME_LABEL[body.outcome]));
      } else if (mode === 'ask') {
        await askOwner(slug, cell.cell_id, question.trim(), selfOwner ? answer.trim() : undefined);
        onSaved(selfOwner ? 'Fact recorded · self-attested' : `Asked ${ownerName} · the cell waits on the answer`);
      } else if (mode === 'interim') {
        await setInterimPosition(slug, cell.cell_id, position.trim());
        onSaved(`Interim position recorded · ${title}`);
      } else if (mode === 'answer') {
        await answerFactQuestion(slug, cell.fact_waiting!.question_id, answer.trim());
        onSaved(`Answer sent to ${asker}`);
      } else if (mode === 'withdraw') {
        await withdrawFactQuestion(slug, cell.fact_waiting!.question_id, note.trim());
        onSaved(`Question withdrawn · ${title} is ready to decide`);
      } else if (mode === 'not_me') {
        await passFactQuestion(slug, cell.fact_waiting!.question_id, { suggested_user_id: suggest || null, note: note.trim() || undefined });
        onSaved(`Sent back to ${asker}${suggest ? ` · suggested ${names.get(suggest) ?? 'someone'}` : ''}`);
      } else if (mode === 'ask_legal') {
        const r = await askLegalAboutCell(slug, cell.cell_id, note.trim() || undefined);
        onSaved(r.reused ? `Already asked ${legalName ?? 'Legal'} · under Waiting on others` : `Asked ${legalName ?? 'Legal'} · under Waiting on others`);
      }
      return true;
    } catch (e) {
      setError(errorText(e, 'That could not be saved.'));
      return false;
    } finally { setBusy(false); }
  };

  const nudge = async () => {
    if (busy || !cell.fact_waiting) return;
    setBusy(true);
    try {
      await nudgeFactQuestion(slug, cell.fact_waiting.question_id);
      setNudged(`Nudged ${ownerName} today`);
    } catch (e) {
      setNudged(e instanceof ApiError && e.status === 429 ? 'Already nudged today' : errorText(e, 'The nudge could not be sent.'));
    } finally { setBusy(false); }
  };

  const err = (k: string) => (errors[k] ? <p className="gp-field-err" id={`${uid}-${k}-err`} role="alert">{errors[k]}</p> : null);
  const caseLaw = isLegal && law.case_law.length ? (
    // Legal only: a chip; the detail behind a disclosure.
    <details className="gp-ai-cl">
      <summary><Chip text={`Case law · ${law.case_law.length}`} tone="outline" /></summary>
      {law.case_law.map((cl) => <p key={cl.link_id} className="gp-ai-effect">{`${cl.case.case_name}: ${cl.reason} · Effect: ${cl.effect_line}`}</p>)}
    </details>
  ) : null;
  const outcomeView = (
    <dl className="gp-ai-sources">
      <div><dt>Outcome</dt><dd>{cellDisplay(cell, law, names.get(cell.fact_waiting?.asked_to_user_id ?? '') ?? null).label}</dd></div>
      {d?.eu_risk_tier ? <div><dt>EU risk tier</dt><dd>{TIER_LABEL[d.eu_risk_tier]}</dd></div> : null}
      {d?.decided_at ? <div><dt>Decided</dt><dd>{`${names.get(d.decided_by ?? '') ?? 'Legal'} · ${fmtDay(d.decided_at)}${d.decision_version ? ` · v${d.decision_version}` : ''}`}</dd></div> : null}
      {d?.reason ? <div><dt>Reason</dt><dd>{d.reason}</dd></div> : d?.privileged_withheld ? <div><dt>Reason</dt><dd>Held by Legal</dd></div> : null}
    </dl>
  );

  // ---- everyone else: read-only, one action (T4-B2) ----
  if (mode === 'view' || mode === 'ask_legal') {
    const needs = cell.open;
    if (mode === 'ask_legal') {
      return (
        <ActionDialog open={open} title={`Ask ${legalName ?? 'Legal'} · ${title}`} submitLabel={`Send to ${legalName ?? 'Legal'}`} onClose={onClose} onSubmit={submit}
          busy={busy} error={error} headerBack={back('view')}>
          <Field id={`${uid}-msg`} label="Your question (optional)">
            <Textarea rows={3} value={note} maxLength={2000} onChange={(e) => setNote(e.currentTarget.value)} />
          </Field>
        </ActionDialog>
      );
    }
    return (
      <ActionDialog open={open} title={needs ? `Needs a decision · ${legalName ?? 'Legal'}` : title} submitLabel="" hideSubmit cancelLabel="Close"
        onClose={onClose} onSubmit={() => false}>
        {needs ? <p className="gp-ai-cellhead">{title}</p> : null}
        {outcomeView}
        {legalName ? <div className="gp-actions"><Button compact onClick={() => go('ask_legal')}>Ask Legal about this</Button></div> : null}
      </ActionDialog>
    );
  }

  // ---- the asked owner (T4-H9: before the Legal hat) ----
  if (mode === 'answer' || mode === 'not_me') {
    const q = cell.fact;
    if (mode === 'not_me') {
      const people = [...names].filter(([id]) => id !== viewerId && id !== owner);
      return (
        <ActionDialog open={open} title={`Not me · ${title}`} submitLabel={`Send to ${asker}`} onClose={onClose} onSubmit={submit} busy={busy} error={error}
          headerBack={back('answer')}>
          <Field id={`${uid}-who`} label="Who should answer?" error={errors.suggest}>
            <Select value={suggest} data-err={`${uid}-suggest`} onChange={(e) => { setSuggest(e.currentTarget.value); setErrors({}); }}>
              <option value="">Not sure</option>
              {people.map(([id, n]) => <option key={id} value={id}>{n}</option>)}
            </Select>
          </Field>
          <Field id={`${uid}-why`} label="Note (optional)">
            <Input value={note} maxLength={500} onChange={(e) => { setNote(e.currentTarget.value); setErrors({}); }} />
          </Field>
        </ActionDialog>
      );
    }
    return (
      <ActionDialog open={open} title={`Question from ${asker} (Legal)`} submitLabel="Send answer" onClose={onClose} onSubmit={submit} busy={busy} error={error}>
        <p className="gp-ai-cellhead">{title}</p>
        {q ? <p className="gp-ai-quote">{`“${q.question_text}”`}</p> : null}
        <Field id={`${uid}-answer`} label="Your answer" required error={errors.answer}>
          <Textarea rows={3} value={answer} data-err={`${uid}-answer`} aria-invalid={!!errors.answer || undefined}
            onChange={(e) => { setAnswer(e.currentTarget.value); setErrors((x) => ({ ...x, answer: '' })); }} />
        </Field>
        <Checkbox id={`${uid}-attest`} checked={attest} data-err={`${uid}-attest`} aria-invalid={!!errors.attest || undefined}
          onChange={(e) => { setAttest(e.currentTarget.checked); setErrors((x) => ({ ...x, attest: '' })); }}
          label="I confirm this answer is accurate for this system" aria-describedby={errors.attest ? `${uid}-attest-err` : undefined} />
        {err('attest')}
        <div className="gp-actions"><Button compact onClick={() => go('not_me')}>Not me — suggest someone</Button></div>
      </ActionDialog>
    );
  }

  // ---- Legal, a question out to the owner (T4-B1) ----
  if (mode === 'waiting' || mode === 'withdraw') {
    const w = cell.fact_waiting!;
    if (mode === 'withdraw') {
      return (
        <ActionDialog open={open} title={`Withdraw question · ${title}`} submitLabel="Withdraw question" danger onClose={onClose} onSubmit={submit}
          busy={busy} error={error} headerBack={back('waiting')}>
          <Field id={`${uid}-wr`} label="Reason" required error={errors.note}>
            <Textarea rows={2} value={note} data-err={`${uid}-note`} onChange={(e) => { setNote(e.currentTarget.value); setErrors({}); }} />
          </Field>
        </ActionDialog>
      );
    }
    return (
      <ActionDialog open={open} title={`Waiting · ${ownerName} · ${title}`} submitLabel="" hideSubmit cancelLabel="Close" onClose={onClose} onSubmit={() => false} error={error}>
        <div className="gp-ai-fact">
          <strong>{`Question to ${ownerName} · asked ${fmtDay(w.asked_at)}`}</strong>
          {cell.fact ? <p className="gp-ai-quote">{`“${cell.fact.question_text}”`}</p> : null}
        </div>
        <div className="gp-actions">
          {nudged ? <Chip text={nudged} tone="lock" /> : <Button compact aria-disabled={busy || undefined} onClick={() => void nudge()}>{`Nudge ${ownerName}`}</Button>}
          <Button compact onClick={() => go('withdraw')}>Withdraw question…</Button>
          {law.in_force ? <Button compact onClick={() => go('interim')}>Record interim position</Button> : null}
        </div>
      </ActionDialog>
    );
  }

  // ---- Legal: interim / ask owner / decide ----
  if (mode === 'interim' || mode === 'ask') {
    return (
      <ActionDialog open={open} title={`${mode === 'interim' ? 'Interim position' : selfOwner ? 'Record a fact' : `Ask ${ownerName}`} · ${title}`}
        submitLabel={mode === 'interim' ? 'Record interim position' : selfOwner ? 'Record fact' : `Ask ${ownerName}`}
        onClose={onClose} onSubmit={submit} busy={busy} error={error} headerBack={back(cell.fact_waiting ? 'waiting' : 'decide')}>
        {mode === 'interim' ? (
          <Field id={`${uid}-interim`} label="Interim legal position" required error={errors.position} hint="Closes the in-force gap; the cell stays open to decide.">
            <Textarea rows={3} value={position} data-err={`${uid}-position`} onChange={(e) => { setPosition(e.currentTarget.value); setErrors({}); }} />
          </Field>
        ) : (
          <>
            <Field id={`${uid}-q`} label={selfOwner ? 'The fact to record' : `Question for ${ownerName}`} required error={errors.question} hint="Stored exactly as written.">
              <Textarea rows={2} value={question} data-err={`${uid}-question`} onChange={(e) => { setQuestion(e.currentTarget.value); setErrors({}); }} />
            </Field>
            {selfOwner ? (
              <Field id={`${uid}-self`} label="Answer · self-attested (you own this system)" required error={errors.answer}>
                <Textarea rows={2} value={answer} data-err={`${uid}-answer`} onChange={(e) => { setAnswer(e.currentTarget.value); setErrors({}); }} />
              </Field>
            ) : null}
          </>
        )}
      </ActionDialog>
    );
  }

  const prev = cell.previous;
  return (
    <ActionDialog open={open} title={title} submitLabel={`Save to v${draftV} draft`} onClose={onClose} onSubmit={submit} busy={busy} error={error} initialFocus="title">
      {ruleChanged ? (
        <Alert variant="warning">
          <p className="gp-ai-alert-line">
            {`Amended${law.amended_at ? ` ${fmtDay(law.amended_at)}` : ''}`}
            {law.source_url ? <> · <a href={law.source_url} target="_blank" rel="noreferrer">View change</a></> : null}
          </p>
          {prev?.outcome ? <p className="gp-ai-alert-line">{`You decided: ${OUTCOME_LABEL[prev.outcome]}${prev.decision_version ? ` · v${prev.decision_version}` : ''}`}</p> : null}
        </Alert>
      ) : null}
      {caseLaw}
      {cell.fact && cell.fact.state === 'answered' ? (
        <div className="gp-ai-fact">
          <strong>{`Fact from ${names.get(cell.fact.asked_to_user_id) ?? 'the owner'}`}</strong>
          <p className="gp-ai-quote">{`“${cell.fact.question_text}”`}</p>
          <p className="gp-ai-quote">{cell.fact.answer_text}</p>
          <span className="gp-dim">{`${cell.fact.self_attested ? 'Self-attested' : 'Attested'}${cell.fact.answered_at ? ` · ${fmtDay(cell.fact.answered_at)}` : ''}`}</span>
        </div>
      ) : null}
      <div className="gp-actions">
        {owner ? <Button compact onClick={() => go('ask')}>{selfOwner ? 'Record a fact (self-attested)' : `Ask ${ownerName}`}</Button> : null}
        {law.in_force && cell.open ? <Button compact onClick={() => go('interim')}>Record interim position</Button> : null}
        {onRelease && d?.state === 'recorded' && d.outcome ? <Button compact onClick={() => onRelease(d.determination_id)}>Release rationale…</Button> : null}
      </div>
      <fieldset className="gp-ai-fieldset" aria-describedby={errors.outcome ? `${uid}-outcome-err` : undefined}>
        <legend>Outcome</legend>
        {OUTCOMES.map((o, i) => (
          <Radio key={o} id={`${uid}-out-${o}`} name={`${uid}-out`} label={OUTCOME_LABEL[o]} checked={outcome === o}
            aria-invalid={!!errors.outcome || undefined} {...(i === 0 ? { 'data-err': `${uid}-outcome` } : {})}
            onChange={() => { setOutcome(o); setErrors((x) => ({ ...x, outcome: '' })); }} />
        ))}
        {err('outcome')}
      </fieldset>
      {outcome === 'applies_with_conditions' ? (
        <Field id={`${uid}-cond`} label="Conditions" required error={errors.conditions}>
          <Textarea rows={2} value={conditions} data-err={`${uid}-conditions`} onChange={(e) => { setConditions(e.currentTarget.value); setErrors((x) => ({ ...x, conditions: '' })); }} />
        </Field>
      ) : null}
      {isEu ? (
        <fieldset className="gp-ai-fieldset" aria-describedby={errors.role ? `${uid}-role-err` : undefined}>
          <legend>EU role</legend>
          <div className="gp-ai-inline">
            {ROLES.map((r, i) => (
              <Radio key={r} id={`${uid}-role-${r}`} name={`${uid}-role`} label={ROLE_LABEL[r]} checked={role === r}
                aria-invalid={!!errors.role || undefined} {...(i === 0 ? { 'data-err': `${uid}-role` } : {})}
                onChange={() => { setRole(r); setErrors((x) => ({ ...x, role: '' })); }} />
            ))}
          </div>
          {err('role')}
        </fieldset>
      ) : null}
      {isEu && applies ? (
        <Field id={`${uid}-tier`} label="EU risk tier" required error={errors.tier}>
          <Select value={tier} data-err={`${uid}-tier`} onChange={(e) => { setTier(e.currentTarget.value as EuRiskTier); setErrors((x) => ({ ...x, tier: '' })); }}>
            <option value="">Choose…</option>
            {TIERS.map((t) => <option key={t} value={t}>{TIER_LABEL[t]}</option>)}
          </Select>
        </Field>
      ) : null}
      <Field id={`${uid}-reason`} label="Reason · one line" required error={errors.reason}>
        <Input value={reason} maxLength={500} data-err={`${uid}-reason`} onChange={(e) => { setReason(e.currentTarget.value); setErrors((x) => ({ ...x, reason: '' })); }} />
      </Field>
      <Field id={`${uid}-vis`} label="Reason visibility">
        <Select value={visibility} onChange={(e) => setVisibility(e.currentTarget.value as typeof visibility)}>
          {(Object.keys(VISIBILITY_LABEL) as Array<keyof typeof VISIBILITY_LABEL>).map((v) => <option key={v} value={v}>{VISIBILITY_LABEL[v]}</option>)}
        </Select>
      </Field>
      {visibility === 'legal_only' ? <p className="gp-ai-priv">Privileged &amp; Confidential</p> : null}
      {history.data && history.data.history.length ? (
        <div>
          <h4 id={`${uid}-hist`} className="gp-ai-h4">History</h4>
          <ul className="gp-ai-history" aria-labelledby={`${uid}-hist`}>
            {history.data.history.map((h) => (
              <li key={h.determination_id}>
                {[h.outcome ? OUTCOME_LABEL[h.outcome] : 'Interim position', STATE_WORD[h.state] ?? h.state,
                  h.decision_version ? `v${h.decision_version}` : null, h.decided_at ? fmtDay(h.decided_at) : null].filter(Boolean).join(' · ')}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </ActionDialog>
  );
}

export { systemName };
